import { Box, Card, CardActionArea, Stack, useTheme } from '@mui/material';
import React, { FC, useMemo } from 'react';
import {
  DeliveryShipmentStatus as TrackingStatus,
  DAYS_COVERED_I18N_KEY,
  buildDaysCoveredI18nParams,
  getDaysCoveredFallback,
  getPriorityShipmentStatus,
} from '@fdha/common-utils';

import { formatUTCDate, MenuType } from '../../utils';
import { ShipmentLabel } from '../../types/index';
import { Typography } from '../Typography/Typography';
import { Icon } from '../Icon/Icon';
import { IconMenu } from '../Menu/IconMenu';
import { DeliveryUpdateInfo } from '../DeliveryUpdateInfo/DeliveryUpdateInfo';

import { DeliveryStatus } from './DeliveryStatus';

export interface DeliveryCardProps {
  buttonLabel: string;
  deliveryId: string;
  deliveryDate: string;
  openChanges: string;
  status: 'open' | 'closed';
  shipmentLabels?: ShipmentLabel[] | null;
  showShipmentStatus?: boolean;
  isCanceled: boolean;
  isOwner: boolean;
  showIconMenu?: boolean;
  weekOfCycle?: string | null;
  isDelayed?: boolean;
  onClick: () => void;
  menuType: MenuType;
  labelHelper?: React.ReactNode;
  updatedAt?: string | null;
  updatedById?: string | null;
  updatedByName?: string | null;
  currentUserId?: string;
  /**
   * Renders "Covers N day(s) · …" via `buildDaysCoveredI18nParams`; hidden
   * when canceled.
   */
  daysCovered?: number;
  weekdaysCovered?: readonly string[];
}

type IconMenuProps = {
  showIconMenu: true;
  onEdit: (id: string) => void;
  onCancel: (id: string) => void;
  onReactivate: (id: string) => void;
};

type IconMenuPropsFalse = {
  showIconMenu?: false;
};

type DeliveryCardPropsWithIconMenu = DeliveryCardProps &
  (IconMenuProps | IconMenuPropsFalse);

const DeliveryCard: FC<DeliveryCardPropsWithIconMenu> = ({
  buttonLabel,
  deliveryId,
  deliveryDate,
  openChanges,
  status,
  shipmentLabels,
  showShipmentStatus,
  isCanceled,
  isOwner,
  weekOfCycle,
  isDelayed,
  onClick,
  menuType,
  labelHelper,
  updatedAt,
  updatedById,
  updatedByName,
  currentUserId,
  daysCovered = 0,
  weekdaysCovered = [],
  ...props
}) => {
  const theme = useTheme();

  const { showIconMenu } = props;

  const editMenuOption = {
    label: 'Edit order',
    testId: 'EDIT_MENU_OPTION',
    icon: 'edit-outline',
    disabled: status === 'closed' || isCanceled ? true : false,
    handleClick: () => showIconMenu && props.onEdit(deliveryId),
  };
  const cancelMenuOption = {
    label: 'Cancel delivery',
    testId: 'CANCEL_MENU_OPTION',
    icon: 'alert-circle-outline',
    handleClick: () => showIconMenu && props.onCancel(deliveryId),
  };
  const reactivateMenuOption = {
    label: 'Reactivate delivery',
    testId: 'REACTIVATE_MENU_OPTION',
    icon: 'alert-circle-outline',
    handleClick: () => showIconMenu && props.onReactivate(deliveryId),
  };

  const deliveryStatus = isCanceled
    ? 'Delivery canceled for'
    : 'Delivery expected for';

  const deliveryShipmentStatus = useMemo(() => {
    if (!showShipmentStatus) {
      return;
    }
    if (!shipmentLabels?.length) {
      return TrackingStatus.BeingPrepared;
    }
    const statuses = shipmentLabels.map((label) => label.trackingStatus);

    return getPriorityShipmentStatus(statuses);
  }, [shipmentLabels, showShipmentStatus]);

  const daysCoveredLabel = useMemo(() => {
    const params = buildDaysCoveredI18nParams(daysCovered, weekdaysCovered, {
      isCanceled,
    });
    if (!params) return null;
    return (
      <Box display="flex" flexDirection="row" alignItems="center">
        <Icon
          name="calendar-outline"
          size="medium"
          fill={theme.palette.warning.dark}
        />
        <Typography
          variant="subtitle2"
          color={theme.palette.text.secondary}
          marginLeft="4px"
          fontWeight={400}
          fontSize={13}
          i18nKey={DAYS_COVERED_I18N_KEY}
          i18nParams={params}
        >
          {getDaysCoveredFallback(params)}
        </Typography>
      </Box>
    );
  }, [
    isCanceled,
    daysCovered,
    weekdaysCovered,
    theme.palette.warning.dark,
    theme.palette.text.secondary,
  ]);

  return (
    <Card
      data-testid={`DELIVERY_CARD_${deliveryId}`}
      elevation={5}
      sx={{ boxShadow: theme.shadows[2], mb: 2 }}
    >
      <Box
        padding={1.5}
        display="flex"
        justifyContent="space-between"
        sx={{ backgroundColor: theme.palette.background.default }}
      >
        <Box>
          <Typography
            variant="body1"
            color={theme.palette.text.secondary}
            fontSize={10}
            fontWeight={500}
          >
            {deliveryStatus}
          </Typography>
          <Stack direction="row" paddingY={1} spacing={1}>
            <Typography fontWeight={500}>
              {formatUTCDate(deliveryDate, 'monthWeekDay')}
            </Typography>
            {labelHelper}
            <Typography
              variant="overline"
              color={theme.palette.text.hint}
              ml={1}
              lineHeight={2}
            >
              {menuType === MenuType.QuickShip
                ? 'QUICK SHIP'
                : `WEEK ${weekOfCycle}`}
            </Typography>
          </Stack>
          <DeliveryStatus
            openChangesDate={openChanges}
            status={status}
            isOwner={isOwner}
            trackingStatus={deliveryShipmentStatus}
            isCanceled={isCanceled}
          />
          {daysCoveredLabel}
          {updatedAt && updatedById && (
            <DeliveryUpdateInfo
              updatedAt={updatedAt}
              updatedByName={updatedByName || ''}
              currentUserId={currentUserId}
              updatedById={updatedById}
            />
          )}
        </Box>
        {showIconMenu && (
          <IconMenu
            items={[
              editMenuOption,
              isCanceled === false ? cancelMenuOption : reactivateMenuOption,
            ]}
          />
        )}
      </Box>
      {isDelayed && (
        <Box
          display="flex"
          justifyContent="center"
          sx={{ backgroundColor: theme.palette.warning.light }}
          py={0.5}
        >
          <Typography variant="caption">
            This delivery might be delayed
          </Typography>
        </Box>
      )}
      <CardActionArea
        onClick={onClick}
        sx={{ padding: 1, textAlign: 'center' }}
      >
        <Typography variant="body2" color={theme.palette.info.main}>
          {buttonLabel}
        </Typography>
      </CardActionArea>
    </Card>
  );
};

export default DeliveryCard;
